import type { RealtimeChannel } from '@supabase/supabase-js';
import { getSupabaseClient } from '../lib/supabase';
import {
  attachResolvedPhotoDelivery,
  isGalleryMediaEligible,
  isVideoPlaybackReady,
} from '../domain/mediaDelivery';
import type { GalleryCursor, GalleryPage, WebMedia } from '../types/media';

const GALLERY_PAGE_SIZE = 60;
const VIDEO_POSTER_CACHE_MS = 10 * 60 * 1000;

const videoPosterCache = new Map<string, { poster: string; expiresAt: number }>();

const GALLERY_MEDIA_COLUMNS = `
  id,
  event_id,
  uploader_id,
  local_client_id,
  storage_path,
  original_url,
  display_url,
  thumbnail_url,
  watermarked_url,
  original_storage_path,
  display_storage_path,
  thumbnail_storage_path,
  watermarked_storage_path,
  storage_provider,
  storage_bucket,
  r2_key,
  cloudflare_image_url,
  cloudflare_stream_uid,
  cloudflare_stream_playback_url,
  cloudflare_stream_thumbnail_url,
  cloudflare_stream_status,
  cloudflare_stream_ready_at,
  upload_status,
  caption,
  width,
  height,
  file_size,
  media_type,
  duration,
  created_at,
  updated_at,
  status,
  lifecycle_state,
  moderation_status,
  uploader:profiles!photos_uploader_id_fkey(
    id,
    full_name,
    email,
    avatar_url
  )
`;

function normalizeRow(row: Record<string, unknown>): WebMedia {
  const uploaderValue = row.uploader;
  const uploader = Array.isArray(uploaderValue) ? uploaderValue[0] ?? null : uploaderValue ?? null;
  return attachResolvedPhotoDelivery({
    ...(row as unknown as WebMedia),
    uploader: uploader as WebMedia['uploader'],
  });
}

function attachVideoPoster(media: WebMedia, poster: string): WebMedia {
  return {
    ...media,
    delivery: {
      thumb: null,
      display: null,
      full: null,
      poster,
      playback: media.delivery?.playback ?? null,
    },
  };
}

async function hydrateSignedVideoPosters(items: WebMedia[]): Promise<WebMedia[]> {
  const now = Date.now();
  const eligibleVideos = items.filter(
    (item) => item.media_type === 'video' && isVideoPlaybackReady(item) && item.cloudflare_stream_uid,
  );
  if (eligibleVideos.length === 0) return items;

  const posterByPhotoId = new Map<string, string>();
  const missingIds: string[] = [];

  for (const video of eligibleVideos) {
    const cached = videoPosterCache.get(video.id);
    if (cached && cached.expiresAt > now) {
      posterByPhotoId.set(video.id, cached.poster);
    } else {
      videoPosterCache.delete(video.id);
      missingIds.push(video.id);
    }
  }

  if (missingIds.length > 0) {
    try {
      const client = getSupabaseClient();
      const { data, error } = await client.functions.invoke('cloudflare-media', {
        body: {
          action: 'stream-playback-urls',
          photoIds: missingIds,
        },
      });
      if (error) throw error;

      const response = data as {
        ok?: boolean;
        items?: Array<{
          ok?: boolean;
          photoId?: string;
          thumbnailUrl?: string;
        }>;
      } | null;

      for (const raw of response?.items ?? []) {
        if (raw.ok !== true) continue;
        const photoId = String(raw.photoId ?? '').trim();
        const poster = String(raw.thumbnailUrl ?? '').trim();
        if (!photoId || !poster) continue;
        posterByPhotoId.set(photoId, poster);
        videoPosterCache.set(photoId, {
          poster,
          expiresAt: Date.now() + VIDEO_POSTER_CACHE_MS,
        });
      }
    } catch (error) {
      console.warn(
        '[web gallery] Signed Stream poster hydration failed:',
        error instanceof Error ? error.message : error,
      );
    }
  }

  return items.map((item) => {
    const poster = posterByPhotoId.get(item.id);
    return poster ? attachVideoPoster(item, poster) : item;
  });
}

export async function loadSignedVideoDelivery(media: WebMedia): Promise<WebMedia> {
  if (!isVideoPlaybackReady(media) || !media.cloudflare_stream_uid) return media;

  const client = getSupabaseClient();
  const { data, error } = await client.functions.invoke('cloudflare-media', {
    body: {
      action: 'stream-playback-url',
      photoId: media.id,
      streamUid: media.cloudflare_stream_uid,
    },
  });
  if (error) throw error;

  const response = data as {
    ok?: boolean;
    playbackUrl?: string;
    thumbnailUrl?: string;
    error?: string;
  } | null;
  const playback = String(response?.playbackUrl ?? '').trim();
  const poster = String(response?.thumbnailUrl ?? '').trim();
  if (!response?.ok || !playback || !poster) {
    throw new Error(response?.error || 'Signed Cloudflare Stream delivery URLs were not returned.');
  }

  videoPosterCache.set(media.id, {
    poster,
    expiresAt: Date.now() + VIDEO_POSTER_CACHE_MS,
  });

  return {
    ...media,
    delivery: {
      thumb: null,
      display: null,
      full: null,
      poster,
      playback,
    },
  };
}

export async function loadGalleryPage(
  eventId: string,
  cursor: GalleryCursor | null = null,
): Promise<GalleryPage> {
  const client = getSupabaseClient();
  let query = client
    .from('photos')
    .select(GALLERY_MEDIA_COLUMNS)
    .eq('event_id', eventId)
    .eq('status', 'active')
    .or('moderation_status.is.null,moderation_status.not.in.("quarantined","rejected")')
    .or('lifecycle_state.is.null,lifecycle_state.not.in.("deleted","hidden","archived","failed")')
    .order('created_at', { ascending: false })
    .order('id', { ascending: false })
    .limit(GALLERY_PAGE_SIZE + 1);

  if (cursor?.created_at && cursor?.id) {
    query = query.or(
      `created_at.lt.${cursor.created_at},and(created_at.eq.${cursor.created_at},id.lt.${cursor.id})`,
    );
  }

  const { data, error } = await query;
  if (error) throw error;

  const rawRows = (data ?? []) as unknown as Record<string, unknown>[];
  const hasMore = rawRows.length > GALLERY_PAGE_SIZE;
  const pageRows = rawRows.slice(0, GALLERY_PAGE_SIZE);
  const normalized = pageRows.map(normalizeRow).filter(isGalleryMediaEligible);
  const items = await hydrateSignedVideoPosters(normalized);
  const last = pageRows[pageRows.length - 1] as { created_at?: string; id?: string } | undefined;

  return {
    items,
    hasMore,
    nextCursor:
      hasMore && last?.created_at && last?.id
        ? { created_at: last.created_at, id: last.id }
        : null,
  };
}

export function subscribeToGalleryChanges(
  eventId: string,
  onChange: (row: Partial<WebMedia> | null) => void,
): () => void {
  const client = getSupabaseClient();
  let channel: RealtimeChannel = client
    .channel(`web-gallery-${eventId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'photos',
        filter: `event_id=eq.${eventId}`,
      },
      (payload) => {
        const row = ((payload as any)?.new ?? (payload as any)?.old ?? null) as Partial<WebMedia> | null;
        onChange(row);
      },
    );

  channel = channel.subscribe();
  return () => {
    void client.removeChannel(channel);
  };
}
